import { useEffect, useRef } from 'react'
import {
    Chart as ChartJS, ArcElement, Tooltip, Legend,
    CategoryScale, LinearScale, BarElement, Title
} from 'chart.js'
import { Doughnut, Bar } from 'react-chartjs-2'
import { diasRestantes, claseSemaforo, formatFecha } from '../data/fechas2026'
import styles from '../CalendarioTributario.module.css'

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title)

export default function DashboardTributario({ datos, nit }) {
    if (!datos || datos.length === 0) {
        return (
            <div className={styles.dashEmpty}>
                <div className={styles.dashEmptyIcon}>📊</div>
                <p className={styles.dashEmptyTitle}>Dashboard ejecutivo</p>
                <p>Ingresa un NIT en la pestaña Semáforo para ver el análisis gráfico</p>
            </div>
        )
    }

    const filas = datos.map(v => {
        const d = diasRestantes(v.f)
        return { ...v, d, cl: claseSemaforo(d, v.f) }
    })

    const cnt   = c => filas.filter(v => v.cl === c).length
    const total = filas.length
    const vencidos = cnt('r'), proximos = cnt('a'), alDia = cnt('v'), sinFecha = cnt('g')
    const pct = n => total > 0 ? Math.round(n/total*100) : 0

    // KPIs
    const kpis = [
        { label: 'Total 2026',   n: total,    cl: 'T', color: '#3a6fa8' },
        { label: '🔴 Vencidas',  n: vencidos, cl: 'R', color: '#e57373', pct: pct(vencidos) },
        { label: '🟡 Próximas',  n: proximos, cl: 'A', color: '#ffd54f', pct: pct(proximos) },
        { label: '🟢 Al día',    n: alDia,    cl: 'V', color: '#66bb6a', pct: pct(alDia)    },
        { label: '⬜ Sin fecha', n: sinFecha, cl: 'G', color: '#bdbdbd', pct: pct(sinFecha) },
    ]

    // Donut
    const donutData = {
        labels: ['🔴 Vencidas','🟡 Próximas','🟢 Al día','⬜ Sin fecha'],
        datasets: [{
            data: [vencidos, proximos, alDia, sinFecha],
            backgroundColor: ['#e57373','#ffd54f','#66bb6a','#bdbdbd'],
            borderWidth: 3, borderColor: 'white', hoverOffset: 6
        }]
    }
    const donutOpts = {
        responsive: true, maintainAspectRatio: false, cutout: '68%',
        plugins: { legend: { position: 'bottom', labels: { font: { size: 11 }, padding: 10 } },
            tooltip: { callbacks: { label: c => `${c.label}: ${c.raw} (${pct(c.raw)}%)` } } }
    }

    // Bar por entidad
    const ents = {}
    filas.forEach(v => {
        if (!ents[v.ent]) ents[v.ent] = {r:0,a:0,v:0,g:0}
        ents[v.ent][v.cl]++
    })
    const entLabels = Object.keys(ents)
    const barEntData = {
        labels: entLabels,
        datasets: [
            { label: '🔴 Vencidas', data: entLabels.map(e=>ents[e].r), backgroundColor: '#e57373' },
            { label: '🟡 Próximas', data: entLabels.map(e=>ents[e].a), backgroundColor: '#ffd54f' },
            { label: '🟢 Al día',   data: entLabels.map(e=>ents[e].v), backgroundColor: '#66bb6a' },
        ]
    }
    const barEntOpts = {
        responsive: true, maintainAspectRatio: false,
        scales: { x: { stacked: true, ticks: { font: { size: 10 } } }, y: { stacked: true, ticks: { stepSize: 1 } } },
        plugins: { legend: { labels: { font: { size: 10 }, padding: 8 } } }
    }

    // Bar por mes
    const meses = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"]
    const porMes = { r: new Array(12).fill(0), a: new Array(12).fill(0), v: new Array(12).fill(0) }
    filas.forEach(v => {
        if (!v.f) return
        const m = parseInt(v.f.split("-")[1]) - 1
        if (v.f.startsWith("2026") && m >= 0 && m < 12 && porMes[v.cl]) porMes[v.cl][m]++
    })
    const barMesData = {
        labels: meses,
        datasets: [
            { label: '🔴 Vencidas', data: porMes.r, backgroundColor: 'rgba(229,115,115,.85)' },
            { label: '🟡 Próximas', data: porMes.a, backgroundColor: 'rgba(255,213,79,.85)'  },
            { label: '🟢 Al día',   data: porMes.v, backgroundColor: 'rgba(102,187,106,.85)' },
        ]
    }
    const barMesOpts = {
        responsive: true, maintainAspectRatio: false,
        scales: { x: { stacked: true, ticks: { font: { size: 11 } } }, y: { stacked: true, beginAtZero: true, ticks: { stepSize: 1 } } },
        plugins: { legend: { labels: { font: { size: 11 }, padding: 10 } } }
    }

    // Timeline próximos 30 días
    const prox = filas.filter(v => v.f && v.d !== null && v.d <= 30).sort((a,b) => a.d - b.d).slice(0, 15)

    return (
        <div className={styles.dash}>
            <div className={styles.dashTitulo}>Análisis tributario · NIT {nit}</div>
            <div className={styles.dashSub}>
                Año gravable 2026 · {total} obligaciones · Actualizado {new Date().toLocaleDateString('es-CO',{day:'2-digit',month:'long',year:'numeric'})}
            </div>

            {/* Alerta vencidos */}
            {vencidos > 0 && (
                <div className={styles.alertVenc}>
                    <span className={styles.alertVencN}>{vencidos}</span>
                    <div className={styles.alertVencTxt}>
                        obligación{vencidos > 1 ? 'es' : ''} vencida{vencidos > 1 ? 's' : ''} — requiere{vencidos > 1 ? 'n' : ''} atención inmediata
                    </div>
                </div>
            )}

            {/* KPIs */}
            <div className={styles.kpiRow}>
                {kpis.map(k => (
                    <div key={k.cl} className={`${styles.kpi} ${styles[`kpi${k.cl}`]}`} style={{borderLeftColor: k.color}}>
                        <span className={styles.kpiN}>{k.n}</span>
                        <span className={styles.kpiL}>{k.label}</span>
                        {k.pct !== undefined && <span className={styles.kpiPct}>{k.pct}% del total</span>}
                    </div>
                ))}
            </div>

            {/* Gráficas */}
            <div className={styles.chartsGrid}>
                <div className={styles.chartCard}>
                    <div className={styles.chartTitle}>🥧 Estado de obligaciones</div>
                    <div className={styles.chartWrap}><Doughnut data={donutData} options={donutOpts} /></div>
                </div>
                <div className={styles.chartCard}>
                    <div className={styles.chartTitle}>📋 Obligaciones por entidad</div>
                    <div className={styles.chartWrap}><Bar data={barEntData} options={barEntOpts} /></div>
                </div>
                <div className={`${styles.chartCard} ${styles.chartFull}`}>
                    <div className={styles.chartTitle}>📅 Vencimientos por mes 2026</div>
                    <div className={`${styles.chartWrap} ${styles.chartTall}`}><Bar data={barMesData} options={barMesOpts} /></div>
                </div>
            </div>

            {/* Timeline */}
            <div className={styles.timelineCard}>
                <div className={styles.timelineTitle}>⏰ Próximos vencimientos (30 días)</div>
                <div className={styles.tlItems}>
                    {prox.length === 0 ? (
                        <div className={styles.tlEmpty}>🎉 No hay vencimientos en los próximos 30 días</div>
                    ) : (
                        prox.map((v,i) => (
                            <div key={i} className={`${styles.tlItem} ${styles[`tl${v.cl.toUpperCase()}`]}`}>
                                <span className={styles.tlFecha}>{formatFecha(v.f)}</span>
                                <span className={styles.tlOb}>{v.ob} · {v.per}</span>
                                <span className={styles.tlDias}>
                                    {v.d === 0 ? '¡HOY!' : v.d < 0 ? `Venció ${Math.abs(v.d)}d` : `${v.d}d`}
                                </span>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    )
}
